import { http, HttpResponse } from 'msw';

import type { PayrollSetupProgressData } from 'services/payroll/queries/usePayrollSetupProgress/usePayrollSetupProgress.interface';
import type { WorkAddressData } from 'services/payroll/queries/useWorkAddress/useWorkAddress.interface';
import type { SetWorkAddressData } from 'services/payroll/mutations/useSetWorkAddress/useSetWorkAddress.interface';
import type { ReasonableCompensationData } from 'services/payroll/queries/useReasonableCompensation/useReasonableCompensation.interface';
import type { FinancialData } from 'services/payroll/queries/useFinancialData/useFinancialData.interface';
import type { SetReasonableCompensationSalaryData } from 'services/payroll/mutations/useSetReasonableCompensationSalary/useSetReasonableCompensationSalary.interface';
import type { MigrationBlocker } from 'modules/Payroll/common/Gusto/parseMigrateErrors';

const SAMPLE_MIGRATION_BLOCKERS: MigrationBlocker[] = [
    { key: 'federal_ein_missing', message: 'Federal EIN is required before migration can continue.' },
    { key: 'company_address_unverified', message: 'Company address must be verified in Gusto before migration.' },
    { key: 'pay_schedule_missing', message: 'A pay schedule must be set in Gusto before migration.' },
    {
        key: 'employee_compensation_missing',
        message: 'At least one employee must have compensation configured.',
    },
];

export const buildMigrationBlockedHandler = (blockers: MigrationBlocker[] = SAMPLE_MIGRATION_BLOCKERS) =>
    http.post('*/payroll/setup-progress/complete-step/', () =>
        HttpResponse.json({ code: 'migration_blocked', blockers }, { status: 422 })
    );

const baseProgressData: PayrollSetupProgressData = {
    current_step: 'payroll_migration',
    step_statuses: {
        intro_question: 'incomplete',
        payroll_migration: 'incomplete',
        review_company_info: 'incomplete',
        company_bank_account: 'incomplete',
        pay_schedule: 'incomplete',
        review_personal_info: 'incomplete',
        work_address: 'incomplete',
        reasonable_compensation_education: 'incomplete',
        financial_business_details: 'incomplete',
        reasonable_compensation: 'incomplete',
        employee_bank_account: 'incomplete',
        federal_tax: 'incomplete',
        state_tax: 'incomplete',
        deductions: 'incomplete',
        sign_documents: 'incomplete',
    },
    is_completed: false,
    is_eligible_for_sdk_setup: true,
    embedded_status: 'qualified',
    setup_route: '',
    onboarding_status: '',
    has_benefits: null,
    has_previous_payroll: true,
    has_retirement_interest: null,
    has_healthcare_interest: null,
    previous_payroll_provider: '',
    anticipated_pay_date: null,
    employee_count: null,
    terminated_employee_count: null,
    previous_pay_schedule: '',
    has_remote_employees: null,
};

export default {
    fetchPayrollSetupProgress: http.get('*/payroll/setup-progress/', () =>
        HttpResponse.json(baseProgressData satisfies PayrollSetupProgressData)
    ),
    setPayrollMigrationData: http.post('*/payroll/setup-progress/set-payroll-migration-data/', () =>
        HttpResponse.json(baseProgressData satisfies PayrollSetupProgressData)
    ),
    setMigrationAnswers: http.post('*/payroll/setup-progress/set-migration-answers/', () =>
        HttpResponse.json(baseProgressData satisfies PayrollSetupProgressData)
    ),
    completePayrollSetupStep: http.post('*/payroll/setup-progress/complete-step/', () =>
        HttpResponse.json(baseProgressData satisfies PayrollSetupProgressData)
    ),
    fetchWorkAddress: http.get('*/payroll/setup-progress/work-address/', () =>
        HttpResponse.json({
            work_address: '123 Main St, Suite 100, San Francisco, CA, 94105',
        } satisfies WorkAddressData)
    ),
    setWorkAddress: http.post('*/payroll/setup-progress/set-work-address/', () =>
        HttpResponse.json({
            work_address: '456 Oak Ave, Suite 200, Austin, TX, 78701',
        } satisfies SetWorkAddressData)
    ),

    fetchFinancialData: http.get('*/payroll/reasonable-compensations/financial-data/', () =>
        HttpResponse.json({
            revenue: '200000.00',
            expenses: '100000.00',
        } satisfies FinancialData)
    ),
    setFinancialData: http.post('*/payroll/reasonable-compensations/financial-data/', () =>
        HttpResponse.json({
            revenue: '200000.00',
            expenses: '100000.00',
        } satisfies FinancialData)
    ),

    fetchReasonableCompensation: http.get('*/payroll/reasonable-compensations/', () =>
        HttpResponse.json({
            uuid: 'story-uuid',
            client_id: 'client-1',
            benchmark_year: 2025,
            benchmark_low_value: '60000.00',
            benchmark_high_value: '100000.00',
            benchmark_suggested_value: '80000.00',
            annual_salary: '0.00',
            is_valid: true,
            is_stale: false,
            created_at: '2025-01-01',
            zip_code: '94105',
            naics_code: '541511',
            number_of_employees: 1,
        } satisfies ReasonableCompensationData)
    ),
    setReasonableCompensationSalary: http.patch('*/payroll/reasonable-compensations/*/set-annual-salary/', () =>
        HttpResponse.json({
            uuid: 'story-uuid',
            client_id: 'client-1',
            benchmark_year: 2025,
            benchmark_low_value: '60000.00',
            benchmark_high_value: '100000.00',
            benchmark_suggested_value: '80000.00',
            annual_salary: '80000.00',
            is_valid: true,
            is_stale: false,
            created_at: '2025-01-01',
        } satisfies SetReasonableCompensationSalaryData)
    ),
};
